import { useState, useEffect, useRef } from "react";
import Dashboard from "./dashboard";
import supabase from "./supabaseClient";
import { Analytics } from "@vercel/analytics/react";

// ─── THEME ───────────────────────────────────────────────────────────────────
const T = {
  bg: "#0e0809",
  surface: "rgba(255,255,255,0.03)",
  surfaceHover: "rgba(255,255,255,0.055)",
  border: "rgba(255,255,255,0.07)",
  borderHover: "rgba(198,145,115,0.35)",
  rose: "#c69173",
  roseLight: "#e0b49a",
  roseDim: "rgba(198,145,115,0.15)",
  cream: "#f5ece4",
  creamDim: "rgba(245,236,228,0.55)",
  creamFaint: "rgba(245,236,228,0.25)",
  red: "#c4556a",
  green: "#7ab898",
  text: "#f5ece4",
  textMid: "rgba(245,236,228,0.6)",
  textFaint: "rgba(245,236,228,0.3)",
  glow: "rgba(198,145,115,0.08)",
};

// ─── STRIPE ───────────────────────────────────────────────────────────────────
const STRIPE_URL = "https://buy.stripe.com/7sY4gzeWJ3pS1gd67Sd7q02";

// ─── QUESTIONS ────────────────────────────────────────────────────────────────
const STEPS = [
  {
    id: "page_type",
    question: "Is your OnlyFans page free or paid?",
    subtitle: "This completely changes your monetization strategy.",
    type: "single",
    options: [
      { label: "Free page", sub: "I rely on PPV & tips" },
      { label: "Paid subscription", sub: "Fans pay monthly to access" },
      { label: "Both", sub: "I run free + a premium tier" },
      { label: "Not live yet", sub: "Still setting up" },
    ],
  },
  {
    id: "sub_price",
    question: "What's your current subscription price?",
    subtitle: "If free, what do you charge for PPV on average?",
    type: "single",
    options: [
      { label: "Free / $0" },
      { label: "$3 – $7" },
      { label: "$8 – $14" },
      { label: "$15 – $25" },
      { label: "$25+" },
    ],
  },
  {
    id: "subscribers",
    question: "How many paying subscribers right now?",
    subtitle: "Be honest — this determines the whole strategy.",
    type: "single",
    options: [
      { label: "0", sub: "Just started or not live" },
      { label: "1 – 20", sub: "Early stage" },
      { label: "21 – 75", sub: "Getting traction" },
      { label: "76 – 200", sub: "Growing" },
      { label: "200+", sub: "Established" },
    ],
  },
  {
    id: "monthly_income",
    question: "Roughly how much are you making per month?",
    subtitle: "Include subs, PPV, tips, and customs.",
    type: "single",
    options: [
      { label: "$0" },
      { label: "Under $100" },
      { label: "$100 – $500" },
      { label: "$500 – $2,000" },
      { label: "$2,000+" },
    ],
  },
  {
    id: "promotion",
    question: "Where do you currently promote your OnlyFans?",
    subtitle: "Select everything you actively use.",
    type: "multi",
    options: [
      { label: "Reddit" },
      { label: "Twitter / X" },
      { label: "TikTok" },
      { label: "Instagram" },
      { label: "Telegram" },
      { label: "Nowhere yet" },
    ],
  },
  {
    id: "content_type",
    question: "What kind of content do you post?",
    subtitle: "This affects which platforms and subreddits are right for you.",
    type: "single",
    options: [
      { label: "Explicit / Adult" },
      { label: "Implied / Suggestive", sub: "Sexy but not explicit — lingerie, teasing" },
      { label: "Fetish / Niche", sub: "Specific kink or category" },
      { label: "Fitness / Wellness" },
      { label: "Cosplay / Alt" },
    ],
  },
  {
    id: "ppv",
    question: "Do you send PPV (pay-per-view) messages to fans?",
    subtitle: "PPV is often where the real money is made.",
    type: "single",
    options: [
      { label: "Never / don't know how" },
      { label: "Occasionally", sub: "A few times a month" },
      { label: "Regularly", sub: "Weekly or more" },
      { label: "It's my main income" },
    ],
  },
  {
    id: "biggest_problem",
    question: "What's your #1 problem right now?",
    subtitle: "Pick the one that keeps you up at night.",
    type: "single",
    options: [
      { label: "Not enough subscribers" },
      { label: "Subscribers don't spend money" },
      { label: "Don't know what to charge" },
      { label: "Burning out on content" },
      { label: "Don't know how to promote" },
      { label: "High churn / people unsubscribing" },
    ],
  },
];

// ─── SYSTEM PROMPT ────────────────────────────────────────────────────────────
function buildPrompt(a) {
  return `You are a ruthlessly practical OnlyFans business coach. You know the platform inside out — PPV mechanics, mass messaging, tip menus, renewal psychology, subreddit strategies, churn reduction, everything. You give specific, honest, actionable advice. No motivational fluff. No vague tips. Real numbers. Real tactics.

CREATOR PROFILE:
- Page type: ${a.page_type}
- Subscription price: ${a.sub_price}
- Paying subscribers: ${a.subscribers}
- Monthly income: ${a.monthly_income}
- Current promotion channels: ${Array.isArray(a.promotion) ? a.promotion.join(", ") : a.promotion}
- Content type: ${a.content_type}
- PPV usage: ${a.ppv}
- Biggest problem: ${a.biggest_problem}

Write their personalized OnlyFans strategy using EXACTLY these 7 section headers (copy them verbatim):

### DIAGNOSIS
MAXIMUM 3 SENTENCES. Not 4. Not 5. Exactly 3. Each sentence must be punchy and specific. Sentence 1: their exact situation in numbers. Sentence 2: the real reason they're stuck (not what they said, the deeper truth). Sentence 3: what that means for their income right now. Do NOT use filler words. Do NOT be gentle. Reference their actual numbers.

### PRICING STRATEGY
Give them exact numbers. What should their sub price be? What should PPV messages cost ($X for photos, $X for videos, $X for customs)? Should they run a free page or paid? What's their optimal tip menu setup? Reference their current niche and subscriber count when giving numbers.

### PPV PLAYBOOK
This is the highest-leverage section. Give them a specific PPV strategy for their situation. How often to send? What script structure works? What content converts best for their niche? Give them 2-3 example PPV message hooks they can use immediately (keep tasteful but effective). Explain the "PPV funnel" — free teaser → PPV offer → upsell to custom.

### PROMOTION PLAN
Based on their content type, give them the exact top 3 places to promote. For Reddit: name specific subreddits that match their niche (be specific — r/actual_subreddit_name). For Twitter: posting cadence and strategy. For TikTok: how to drive without getting banned. Be concrete about what to post where.

### DO THIS TODAY
Exactly 4 actions they can complete in the next 3 hours. Number them 1-4. Be ultra-specific — not "post on Reddit" but "Post a teaser photo to r/[subreddit] with this caption style: [example]". Each action should take 15-30 minutes.

### 7-DAY PLAN
Day by day. Day 1 through Day 7. One focused task per day. Each task should build on the last. By day 7 they should have a complete promotion + monetization system running.

### REAL TALK
The one thing nobody tells creators in their exact situation. Be honest, not harsh. This should feel like advice from a friend who knows the industry — something that reframes their thinking or addresses a blind spot specific to their numbers and problem.

Keep each section focused and specific to their profile. No generic advice. If they have 0 subscribers, speak to that reality. If they're making $0, acknowledge it and address it directly. The advice should feel like it was written for this exact person.`;
}

// ─── PARSE PLAN ───────────────────────────────────────────────────────────────
function parsePlan(text) {
  const keys = ["diagnosis", "pricing", "ppv", "promotion", "today", "week", "realtalk"];
  const headers = {
    diagnosis: /###\s*DIAGNOSIS/i,
    pricing: /###\s*PRICING STRATEGY/i,
    ppv: /###\s*PPV PLAYBOOK/i,
    promotion: /###\s*PROMOTION PLAN/i,
    today: /###\s*DO THIS TODAY/i,
    week: /###\s*7-DAY PLAN/i,
    realtalk: /###\s*REAL TALK/i,
  };

  const lines = text.split("\n");
  let current = null;
  const buffers = {};
  keys.forEach(k => buffers[k] = []);

  for (const line of lines) {
    let matched = false;
    for (const [key, rx] of Object.entries(headers)) {
      if (rx.test(line)) { current = key; matched = true; break; }
    }
    if (!matched && current) buffers[current].push(line);
  }

  const result = {};
  for (const k of keys) {
    const content = buffers[k].join("\n").trim();
    if (content) result[k] = content;
  }

  if (Object.keys(result).length === 0) result.diagnosis = text.trim();
  return result;
}

// ─── API CALL ─────────────────────────────────────────────────────────────────
async function fetchPlan(answers) {
  const res = await fetch("/api/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      messages: [{ role: "user", content: buildPrompt(answers) }],
    }),
  });

  if (!res.ok) {
    throw new Error(`API error: ${res.status}`);
  }

  const data = await res.json();
  const text = data.content?.map(b => b.text || "").join("\n") || "";

  if (!text.trim()) {
    throw new Error("Empty response from API");
  }

  return text;
}

// ─── ATOMS ───────────────────────────────────────────────────────────────────
function Pill({ children }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "6px",
      background: T.roseDim, border: `1px solid rgba(198,145,115,0.2)`,
      borderRadius: "20px", padding: "4px 12px",
      color: T.rose, fontSize: "11px", letterSpacing: "2px",
      fontFamily: "'DM Mono', monospace", textTransform: "uppercase",
    }}>{children}</span>
  );
}

function Divider() {
  return <div style={{ height: "1px", background: T.border, margin: "0 0 20px" }} />;
}

// ─── PROGRESS ─────────────────────────────────────────────────────────────────
function Progress({ step, total }) {
  return (
    <div style={{ marginBottom: "32px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
        <span style={{ color: T.rose, fontFamily: "'DM Mono', monospace", fontSize: "11px", letterSpacing: "2px" }}>
          STEP {step} OF {total}
        </span>
        <span style={{ color: T.textFaint, fontFamily: "'DM Mono', monospace", fontSize: "11px" }}>
          {Math.round((step / total) * 100)}%
        </span>
      </div>
      <div style={{ height: "2px", background: "rgba(255,255,255,0.06)", borderRadius: "1px" }}>
        <div style={{
          height: "100%", borderRadius: "1px",
          width: `${(step / total) * 100}%`,
          background: `linear-gradient(90deg, ${T.rose}, ${T.roseLight})`,
          transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
          boxShadow: `0 0 8px rgba(198,145,115,0.4)`,
        }} />
      </div>
    </div>
  );
}

// ─── STEP ─────────────────────────────────────────────────────────────────────
function Step({ step, onAnswer, answers }) {
  const init = answers[step.id] ?? (step.type === "multi" ? [] : null);
  const [selected, setSelected] = useState(init);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    setSelected(answers[step.id] ?? (step.type === "multi" ? [] : null));
    const t = setTimeout(() => setEntered(true), 30);
    return () => { clearTimeout(t); setEntered(false); };
  }, [step.id]);

  const toggle = (label) => {
    if (step.type === "multi") {
      setSelected(p => p.includes(label) ? p.filter(x => x !== label) : [...p, label]);
    } else {
      setSelected(label);
    }
  };

  const canGo = step.type === "multi" ? selected.length > 0 : selected !== null;
  const cols = step.options.length <= 4 ? 1 : 2;

  return (
    <div style={{
      opacity: entered ? 1 : 0,
      transform: entered ? "translateY(0)" : "translateY(14px)",
      transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)",
    }}>
      <div style={{ marginBottom: "6px" }}>
        <Pill>{step.type === "multi" ? "select all that apply" : "choose one"}</Pill>
      </div>
      <h2 style={{
        fontSize: "clamp(20px,3.5vw,28px)", fontWeight: "700",
        color: T.cream, fontFamily: "'Cormorant Garamond', serif",
        lineHeight: 1.25, margin: "12px 0 6px",
      }}>{step.question}</h2>
      <p style={{ color: T.textFaint, fontSize: "13px", marginBottom: "24px", fontFamily: "'DM Mono', monospace" }}>
        {step.subtitle}
      </p>

      <div className="option-grid" style={{
        display: "grid",
        gridTemplateColumns: cols === 2 ? "1fr 1fr" : "1fr",
        gap: "8px", marginBottom: "28px",
      }}>
        {step.options.map((opt) => {
          const active = step.type === "multi"
            ? selected.includes(opt.label)
            : selected === opt.label;
          return (
            <button key={opt.label} onClick={() => toggle(opt.label)} style={{
              padding: opt.sub ? "14px 14px" : "16px 14px",
              borderRadius: "10px",
              border: `1.5px solid ${active ? T.rose : T.border}`,
              background: active ? T.roseDim : T.surface,
              color: active ? T.roseLight : T.textMid,
              textAlign: "left", cursor: "pointer",
              transition: "all 0.18s",
              transform: active ? "scale(1.015)" : "scale(1)",
              boxShadow: active ? `0 0 16px rgba(198,145,115,0.12)` : "none",
              minHeight: "52px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{
                  width: "16px", height: "16px", borderRadius: step.type === "multi" ? "4px" : "50%",
                  border: `1.5px solid ${active ? T.rose : "rgba(255,255,255,0.15)"}`,
                  background: active ? T.rose : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, fontSize: "9px", color: "#0e0809", fontWeight: "bold",
                  transition: "all 0.18s",
                }}>{active ? "✓" : ""}</span>
                <div>
                  <div style={{ fontSize: "13px", fontFamily: "'DM Mono', monospace", fontWeight: "500" }}>
                    {opt.label}
                  </div>
                  {opt.sub && (
                    <div style={{ fontSize: "11px", color: active ? T.rose : T.textFaint, marginTop: "1px", fontFamily: "'DM Mono', monospace" }}>
                      {opt.sub}
                    </div>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <button disabled={!canGo} onClick={() => canGo && onAnswer(step.id, selected)} style={{
        width: "100%", padding: "15px",
        borderRadius: "10px", border: "none",
        background: canGo
          ? `linear-gradient(135deg, ${T.rose}, ${T.roseLight})`
          : "rgba(255,255,255,0.06)",
        color: canGo ? "#0e0809" : T.textFaint,
        fontSize: "12px", fontWeight: "700",
        fontFamily: "'DM Mono', monospace",
        letterSpacing: "2px", cursor: canGo ? "pointer" : "not-allowed",
        transition: "all 0.2s",
        boxShadow: canGo ? `0 4px 20px rgba(198,145,115,0.25)` : "none",
      }}>
        CONTINUE →
      </button>
    </div>
  );
}

// ─── LOADER ───────────────────────────────────────────────────────────────────
function Loader() {
  const msgs = [
    "Reading your situation...",
    "Analyzing your subscriber count...",
    "Mapping PPV opportunities...",
    "Researching subreddits for your niche...",
    "Calculating your pricing sweet spot...",
    "Building your 7-day plan...",
    "Finalizing your strategy...",
  ];
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [slowWarning, setSlowWarning] = useState(false);

  useEffect(() => {
    const m = setInterval(() => setIdx(x => Math.min(x + 1, msgs.length - 1)), 2000);
    const slow = setTimeout(() => setSlowWarning(true), 5000);
    return () => { clearInterval(m); clearTimeout(slow); };
  }, []);

  useEffect(() => {
    const step = setInterval(() => {
      setProgress(p => p < 98 ? p + 0.18 : p);
    }, 50);
    return () => clearInterval(step);
  }, []);

  return (
    <div style={{ padding: "48px 0", textAlign: "center" }}>
      <div style={{ position: "relative", width: "56px", height: "56px", margin: "0 auto 28px" }}>
        <div style={{
          position: "absolute", inset: 0,
          border: `1.5px solid rgba(198,145,115,0.15)`,
          borderTop: `1.5px solid ${T.rose}`,
          borderRadius: "50%",
          animation: "spin 0.9s linear infinite",
        }} />
        <div style={{
          position: "absolute", inset: "8px",
          border: `1px solid rgba(198,145,115,0.08)`,
          borderBottom: `1px solid ${T.rose}`,
          borderRadius: "50%",
          animation: "spin 1.4s linear infinite reverse",
        }} />
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "'DM Mono', monospace", fontSize: "9px",
          color: T.rose, fontWeight: "500",
        }}>
          {Math.round(progress)}%
        </div>
      </div>

      <p style={{
        color: T.rose, fontFamily: "'DM Mono', monospace",
        fontSize: "12px", letterSpacing: "1px",
        minHeight: "20px", marginBottom: "8px",
        transition: "opacity 0.3s",
      }}>
        {msgs[idx]}
      </p>

      {slowWarning && (
        <p style={{
          color: T.textFaint, fontFamily: "'DM Mono', monospace",
          fontSize: "10px", marginBottom: "12px", letterSpacing: "0.5px",
          animation: "fadeIn 0.5s ease",
        }}>
          Almost there — we go deep so the advice is actually useful.
        </p>
      )}

      <div style={{
        width: "100%", maxWidth: "220px", margin: "0 auto",
        height: "2px", background: "rgba(198,145,115,0.12)",
        borderRadius: "2px", overflow: "hidden",
      }}>
        <div style={{
          height: "100%", borderRadius: "2px",
          background: `linear-gradient(90deg, ${T.rose}, ${T.roseLight})`,
          width: `${progress}%`,
          transition: "width 0.1s linear",
        }} />
      </div>
    </div>
  );
}

// ─── MARKDOWN RENDERER ───────────────────────────────────────────────────────
function RenderContent({ text }) {
  const lines = text.split("\n").filter(l => l.trim() !== "");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {lines.map((line, i) => {
        const numMatch = line.match(/^(\d+)\.\s+(.*)/);
        if (numMatch) {
          return (
            <div key={i} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
              <span style={{
                minWidth: "22px", height: "22px", borderRadius: "50%",
                background: T.roseDim, border: `1px solid rgba(198,145,115,0.25)`,
                color: T.rose, fontSize: "10px", fontFamily: "'DM Mono', monospace",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, marginTop: "1px",
              }}>{numMatch[1]}</span>
              <span style={{ color: T.creamDim, fontSize: "13px", lineHeight: "1.65", fontFamily: "'DM Sans', sans-serif" }}>
                {renderInline(numMatch[2])}
              </span>
            </div>
          );
        }

        const bulletMatch = line.match(/^[-•]\s+(.*)/);
        if (bulletMatch) {
          return (
            <div key={i} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
              <span style={{ color: T.rose, fontSize: "12px", marginTop: "3px", flexShrink: 0 }}>✦</span>
              <span style={{ color: T.creamDim, fontSize: "13px", lineHeight: "1.65", fontFamily: "'DM Sans', sans-serif" }}>
                {renderInline(bulletMatch[1])}
              </span>
            </div>
          );
        }

        const boldHeader = line.match(/^\*\*([^*]+)\*\*:?\s*(.*)/);
        if (boldHeader) {
          return (
            <div key={i}>
              <span style={{ color: T.roseLight, fontFamily: "'DM Mono', monospace", fontSize: "11px", letterSpacing: "1px", textTransform: "uppercase" }}>
                {boldHeader[1]}
              </span>
              {boldHeader[2] && (
                <span style={{ color: T.creamDim, fontSize: "13px", lineHeight: "1.65", fontFamily: "'DM Sans', sans-serif" }}>
                  {" "}{renderInline(boldHeader[2])}
                </span>
              )}
            </div>
          );
        }

        const dayMatch = line.match(/^(Day \d+)[:\s—–]+(.*)/i);
        if (dayMatch) {
          return (
            <div key={i} style={{
              display: "flex", gap: "10px", alignItems: "flex-start",
              background: "rgba(198,145,115,0.05)", borderRadius: "8px",
              padding: "8px 10px",
            }}>
              <span style={{
                color: T.rose, fontFamily: "'DM Mono', monospace",
                fontSize: "10px", letterSpacing: "1px", minWidth: "38px",
                marginTop: "2px", flexShrink: 0,
              }}>{dayMatch[1].toUpperCase()}</span>
              <span style={{ color: T.creamDim, fontSize: "13px", lineHeight: "1.6", fontFamily: "'DM Sans', sans-serif" }}>
                {renderInline(dayMatch[2])}
              </span>
            </div>
          );
        }

        return (
          <p key={i} style={{ color: T.creamDim, fontSize: "13px", lineHeight: "1.7", fontFamily: "'DM Sans', sans-serif", margin: 0 }}>
            {renderInline(line)}
          </p>
        );
      })}
    </div>
  );
}

function renderInline(text) {
  const parts = text.split(/\*\*([^*]+)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1
      ? <span key={i} style={{ color: T.cream, fontWeight: "600" }}>{part}</span>
      : part
  );
}

// ─── PLAN CARD ────────────────────────────────────────────────────────────────
function PlanCard({ emoji, title, content, delay, highlight }) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, []);

  const copy = () => {
    navigator.clipboard.writeText(content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(16px)",
      transition: "all 0.5s cubic-bezier(0.4,0,0.2,1)",
      background: highlight ? `rgba(198,145,115,0.05)` : T.surface,
      border: `1px solid ${highlight ? "rgba(198,145,115,0.22)" : T.border}`,
      borderRadius: "12px",
      marginBottom: "8px",
      overflow: "hidden",
    }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          padding: "13px 16px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          cursor: "pointer",
          borderBottom: open ? `1px solid ${T.border}` : "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
          <span style={{ fontSize: "15px" }}>{emoji}</span>
          <span style={{
            color: highlight ? T.roseLight : T.rose,
            fontFamily: "'DM Mono', monospace",
            fontSize: "10px", letterSpacing: "2px", textTransform: "uppercase",
          }}>{title}</span>
        </div>
        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          <button onClick={e => { e.stopPropagation(); copy(); }} style={{
            background: "none", border: `1px solid ${T.border}`,
            borderRadius: "5px", padding: "3px 9px",
            color: copied ? T.green : T.textFaint,
            fontSize: "9px", fontFamily: "'DM Mono', monospace",
            cursor: "pointer", letterSpacing: "1px",
          }}>
            {copied ? "✓" : "COPY"}
          </button>
          <span style={{ color: T.textFaint, fontSize: "11px", marginLeft: "2px" }}>
            {open ? "▲" : "▼"}
          </span>
        </div>
      </div>

      {open && (
        <div style={{ padding: "14px 16px" }}>
          <RenderContent text={content} />
        </div>
      )}
    </div>
  );
}

// ─── LOCKED CARD ─────────────────────────────────────────────────────────────
function LockedCard({ emoji, title, teaser, onUnlock, delay }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), delay); return () => clearTimeout(t); }, []);

  return (
    <div style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(16px)",
      transition: "all 0.5s cubic-bezier(0.4,0,0.2,1)",
      border: `1px solid ${T.border}`,
      borderRadius: "12px", marginBottom: "8px", overflow: "hidden",
      position: "relative",
    }}>
      <div style={{
        padding: "13px 16px", display: "flex", alignItems: "center",
        justifyContent: "space-between", borderBottom: `1px solid ${T.border}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
          <span style={{ fontSize: "15px" }}>{emoji}</span>
          <span style={{ color: T.textFaint, fontFamily: "'DM Mono', monospace", fontSize: "10px", letterSpacing: "2px", textTransform: "uppercase" }}>
            {title}
          </span>
        </div>
        <span style={{ fontSize: "13px" }}>🔒</span>
      </div>

      <div style={{ padding: "14px 16px", position: "relative" }}>
        <div style={{ filter: "blur(5px)", userSelect: "none", pointerEvents: "none", opacity: 0.4 }}>
          <p style={{ color: T.creamDim, fontSize: "13px", lineHeight: "1.7", fontFamily: "'DM Sans', sans-serif" }}>
            {teaser}
          </p>
        </div>
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "linear-gradient(to bottom, transparent, rgba(14,8,9,0.7))",
        }}>
          <button onClick={onUnlock} style={{
            padding: "11px 28px", borderRadius: "8px",
            background: `linear-gradient(135deg, ${T.rose}, ${T.roseLight})`,
            border: "none", color: "#0e0809",
            fontFamily: "'DM Mono', monospace", fontSize: "11px",
            fontWeight: "700", letterSpacing: "1.5px", cursor: "pointer",
            boxShadow: `0 4px 16px rgba(198,145,115,0.35)`,
            whiteSpace: "nowrap",
          }}>
            UNLOCK →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── UPGRADE MODAL ────────────────────────────────────────────────────────────
function UpgradeModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [joined, setJoined] = useState(false);

  const handleUpgrade = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      setEmailError("Please enter a valid email.");
      return;
    }
    setSaving(true);
    setEmailError("");

    // Product paused: checkout is disabled — save to the waitlist instead.
    try {
      await fetch("/api/save-waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
    } catch (e) {
      console.error("save-waitlist failed, continuing:", e);
    }

    setSaving(false);
    setJoined(true);
  };

  const inp = {
    width: "100%", padding: "11px 14px", marginBottom: "6px",
    background: "rgba(255,255,255,0.05)", border: "1px solid rgba(198,145,115,0.25)",
    borderRadius: "8px", color: "#f5ece4", fontSize: "13px",
    fontFamily: "'DM Mono', monospace", outline: "none",
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(14,8,9,0.88)", backdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "20px",
    }} onClick={onClose}>
      <div style={{
        background: "#1a0f11", border: `1px solid rgba(198,145,115,0.25)`,
        borderRadius: "16px", padding: "28px 24px", width: "100%", maxWidth: "380px",
        boxShadow: "0 32px 64px rgba(0,0,0,0.6)",
        maxHeight: "90vh", overflowY: "auto",
      }} onClick={e => e.stopPropagation()}>

        {(() => {
          const base = 847;
          const key = "ofc_count_seed";
          let seed;
          try { seed = parseInt(localStorage.getItem(key) || "0");
            if (!seed) { seed = Math.floor(Math.random() * 12); localStorage.setItem(key, seed); }
          } catch { seed = 0; }
          const hoursSinceLaunch = Math.floor((Date.now() - new Date("2026-01-01").getTime()) / 3600000);
          const count = base + seed + Math.floor(hoursSinceLaunch / 6);
          return (
            <div style={{ background: "rgba(122,184,152,0.08)", border: "1px solid rgba(122,184,152,0.18)", borderRadius: "8px", padding: "8px 12px", marginBottom: "20px", textAlign: "center" }}>
              <span style={{ color: "#7ab898", fontFamily: "'DM Mono',monospace", fontSize: "10px", letterSpacing: "1px" }}>
                ✦ {count.toLocaleString()} creators using this system this month
              </span>
            </div>
          );
        })()}

        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <div style={{ fontSize: "28px", marginBottom: "10px" }}>🔓</div>
          <h3 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "24px", color: "#f5ece4", fontWeight: "700", marginBottom: "6px",
          }}>
            Unlock Your Full Plan
          </h3>
          <p style={{ color: "rgba(245,236,228,0.45)", fontFamily: "'DM Mono', monospace", fontSize: "10px", lineHeight: "1.7" }}>
            4 strategies ready — personalized to your exact situation
          </p>
        </div>

        {[
          { emoji: "💰", text: "Exact pricing — sub price, PPV, tip menu" },
          { emoji: "🔥", text: "PPV Playbook — word-for-word scripts & hooks" },
          { emoji: "📣", text: "Promotion Plan — exact subreddits for your niche" },
          { emoji: "📅", text: "7-Day Action Plan — day by day execution" },
        ].map((item, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: "10px",
            marginBottom: "8px", padding: "8px 10px",
            background: "rgba(198,145,115,0.05)", borderRadius: "7px",
          }}>
            <span style={{ fontSize: "14px" }}>{item.emoji}</span>
            <span style={{ color: "rgba(245,236,228,0.7)", fontSize: "12px", fontFamily: "'DM Sans', sans-serif" }}>{item.text}</span>
          </div>
        ))}

        <div style={{ margin: "16px 0", padding: "12px 14px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "10px" }}>
          <p style={{ color: "rgba(245,236,228,0.6)", fontFamily: "'DM Sans',sans-serif", fontSize: "12px", lineHeight: "1.6", fontStyle: "italic", marginBottom: "6px" }}>
            "Went from $80 to $340 in 3 weeks just following the PPV playbook. Worth every penny."
          </p>
          <span style={{ color: "rgba(245,236,228,0.3)", fontFamily: "'DM Mono',monospace", fontSize: "10px" }}>— Sofia M. · 47 subs</span>
        </div>

        <div style={{ margin: "16px 0 12px", textAlign: "center" }}>
          <span style={{ color: "rgba(245,236,228,0.5)", fontFamily: "'DM Mono', monospace", fontSize: "10px", letterSpacing: "0.5px", lineHeight: "1.7" }}>
            New signups are paused while we build the next version.<br/>Join the waitlist and we'll email you at launch.
          </span>
        </div>

        {joined ? (
          <div style={{
            background: "rgba(122,184,152,0.1)", border: "1px solid rgba(122,184,152,0.25)",
            borderRadius: "10px", padding: "16px", textAlign: "center",
            color: "#7ab898", fontFamily: "'DM Mono', monospace", fontSize: "11px", letterSpacing: "1px",
          }}>
            ✓ You're on the list — we'll email you at launch.
          </div>
        ) : (<>
          <input
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={e => { setEmail(e.target.value); setEmailError(""); }}
            onKeyDown={e => e.key === "Enter" && handleUpgrade()}
            style={inp}
          />
          {emailError && (
            <p style={{ color: "#c4556a", fontFamily: "'DM Mono', monospace", fontSize: "10px", marginBottom: "8px" }}>
              {emailError}
            </p>
          )}

          <button onClick={handleUpgrade} disabled={saving} style={{
            width: "100%", padding: "15px",
            background: saving ? "rgba(198,145,115,0.4)" : `linear-gradient(135deg, #c69173, #e0b49a)`,
            border: "none", borderRadius: "10px",
            color: "#0e0809", fontFamily: "'DM Mono', monospace",
            fontSize: "12px", fontWeight: "700", letterSpacing: "2px",
            cursor: saving ? "not-allowed" : "pointer", marginTop: "4px",
            boxShadow: "0 4px 20px rgba(198,145,115,0.35)",
          }}>
            {saving ? "SAVING..." : "JOIN THE WAITLIST, LAUNCHING SOON"}
          </button>
        </>)}

        <button onClick={onClose} style={{
          width: "100%", marginTop: "10px", padding: "11px",
          background: "none", border: "none",
          color: "rgba(245,236,228,0.25)", fontFamily: "'DM Mono', monospace",
          fontSize: "10px", cursor: "pointer", letterSpacing: "1px",
        }}>
          maybe later
        </button>
      </div>
    </div>
  );
}

// ─── DYNAMIC UPSELL ──────────────────────────────────────────────────────────
function getDynamicUpsell(answers) {
  const income = answers.monthly_income || "";
  const subs = answers.subscribers || "";
  const problem = answers.biggest_problem || "";
  const ppv = answers.ppv || "";

  if (subs === "0") {
    return {
      label: "HERE'S THE REALITY",
      headline: <>Most creators starting from zero hit <span style={{ color: T.rose }}>$300–$800/mo</span> within 60 days — with the right system.</>,
      sub: "Your pricing, subreddits, and first PPV script are ready. This is the exact playbook for getting your first 50 paying fans.",
    };
  }

  if ((income === "$0" || income === "Under $100") && subs !== "0") {
    return {
      label: "YOUR SUBSCRIBERS AREN'T SPENDING",
      headline: <>You have fans but they're not paying — you're likely leaving <span style={{ color: T.rose }}>$400–$900/mo</span> on the table.</>,
      sub: "The fix is almost always PPV pricing and messaging. Your playbook is ready — it includes the exact scripts to turn lurkers into buyers.",
    };
  }

  if (problem === "High churn / people unsubscribing") {
    return {
      label: "CHURN IS KILLING YOUR GROWTH",
      headline: <>Every unsub you're losing costs you <span style={{ color: T.rose }}>$50–$200/mo</span> in compounding revenue.</>,
      sub: "Your plan includes a retention system — renewal messaging, content pacing, and the psychological triggers that keep fans subscribed.",
    };
  }

  if (ppv === "Never / don't know how") {
    return {
      label: "YOU'RE MISSING YOUR BIGGEST INCOME SOURCE",
      headline: <>Creators at your level who add PPV typically see a <span style={{ color: T.rose }}>2–4× jump in monthly revenue</span>.</>,
      sub: "Your PPV playbook is ready — including word-for-word message hooks you can send today, priced exactly for your subscriber count.",
    };
  }

  if (income === "$500 – $2,000" || income === "$2,000+") {
    return {
      label: "BASED ON YOUR NUMBERS",
      headline: <>Creators at your level who optimize pricing and PPV typically add <span style={{ color: T.rose }}>$500–$1,500/mo</span> within 30 days.</>,
      sub: "Your plan focuses on maximizing spend-per-fan and plugging the revenue leaks that are holding back your next income level.",
    };
  }

  if (problem === "Don't know how to promote") {
    return {
      label: "BASED ON YOUR SITUATION",
      headline: <>The right promotion strategy for your content type could add <span style={{ color: T.rose }}>$300–$700/mo</span> in new subscribers.</>,
      sub: "Your plan includes the exact subreddits, posting schedule, and caption formulas for your niche — ready to use today.",
    };
  }

  return {
    label: "BASED ON YOUR SITUATION",
    headline: <>You're likely leaving <span style={{ color: T.rose }}>$300–$700/mo</span> on the table.</>,
    sub: "Your pricing, PPV strategy, and promotion plan are ready. Unlock them to start fixing it today.",
  };
}

// ─── PLAN VIEW ────────────────────────────────────────────────────────────────
const TEASERS = {
  pricing: "Based on your subscriber count and content type, your sub price should be $X/month. PPV photos at $X, videos at $X, customs at $X. Your tip menu needs these 5 price points to maximize spend per fan...",
  ppv: "Send PPV at $15-25 for photos, $30-50 for videos. Use this 3-line hook: Line 1 curiosity, Line 2 specifics, Line 3 urgency. Example: 'Finished editing my shoot last night — 14 photos, way less fabric than usual. Unlocking for 24hrs only.'",
  promotion: "Post to r/[your niche] daily. Rotate 3 posts across different subreddits at 9am, 2pm, 8pm EST. Caption formula: [character] + action + question to drive comments...",
  week: "Day 1: Fix your profile bio with this formula. Day 2: Send your first mass PPV. Day 3: Post to 5 subreddits. Day 4: Follow up with non-openers. Day 5: Launch a custom content offer...",
};

function Plan({ sections, answers, onReset, paid }) {
  const [showModal, setShowModal] = useState(false);
  const upsell = getDynamicUpsell(answers);

  const freeConfig = [
    { key: "diagnosis", emoji: "🔍", title: "Your Diagnosis", highlight: true },
    { key: "today", emoji: "⚡", title: "Do This Today", highlight: false },
  ];

  const lockedConfig = [
    { key: "pricing", emoji: "💰", title: "Pricing Strategy" },
    { key: "ppv", emoji: "🔥", title: "PPV Playbook" },
    { key: "promotion", emoji: "📣", title: "Promotion Plan" },
    { key: "week", emoji: "📅", title: "7-Day Plan" },
  ];

  useEffect(() => {
    if (paid) return;
    const handleExit = (e) => {
      if (e.clientY < 60) setShowModal(true);
    };
    document.addEventListener("mouseleave", handleExit);
    return () => document.removeEventListener("mouseleave", handleExit);
  }, [paid]);

  const [showUrgency, setShowUrgency] = useState(false);
  useEffect(() => {
    if (paid) return;
    const t = setTimeout(() => setShowUrgency(true), 30000);
    return () => clearTimeout(t);
  }, [paid]);

  return (
    <div>
      {showModal && <UpgradeModal onClose={() => setShowModal(false)} />}

      {showUrgency && !paid && (
        <div style={{
          background: "rgba(198,145,115,0.12)", border: `1px solid rgba(198,145,115,0.35)`,
          borderRadius: "10px", padding: "10px 14px", marginBottom: "14px",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px",
        }}>
          <span style={{ color: T.rose, fontFamily: "'DM Mono',monospace", fontSize: "10px", letterSpacing: "0.5px", lineHeight: "1.5" }}>
            ⏳ Your free diagnosis is ready — your full plan is waiting
          </span>
          <button onClick={() => setShowModal(true)} style={{
            background: `linear-gradient(135deg, ${T.rose}, ${T.roseLight})`,
            border: "none", borderRadius: "6px", padding: "7px 14px",
            color: "#0e0809", fontFamily: "'DM Mono',monospace", fontSize: "9px",
            fontWeight: "700", letterSpacing: "1px", cursor: "pointer", whiteSpace: "nowrap",
          }}>UNLOCK →</button>
        </div>
      )}

      <div style={{ marginBottom: "20px" }}>
        <Pill>Your personalized strategy</Pill>
        <h2 style={{
          fontSize: "clamp(20px,4vw,28px)", fontWeight: "700",
          color: T.cream, fontFamily: "'Cormorant Garamond', serif",
          margin: "10px 0 4px", lineHeight: 1.2,
        }}>
          Here's your diagnosis.
        </h2>
        <p style={{ color: T.textFaint, fontFamily: "'DM Mono', monospace", fontSize: "11px" }}>
          Personalized for your exact situation
        </p>
      </div>

      <Divider />

      {sections.today && !paid && (
        <div style={{
          background: "rgba(198,145,115,0.06)", border: `1px solid rgba(198,145,115,0.2)`,
          borderRadius: "10px", padding: "10px 14px", marginBottom: "12px",
          display: "flex", alignItems: "center", gap: "10px",
        }}>
          <span style={{ fontSize: "16px" }}>⚡</span>
          <span style={{ color: T.rose, fontFamily: "'DM Mono',monospace", fontSize: "10px", letterSpacing: "1px" }}>
            YOUR FIRST ACTION IS READY BELOW — takes 30 minutes
          </span>
        </div>
      )}

      {freeConfig.map(({ key, emoji, title, highlight }, i) =>
        sections[key] ? (
          <PlanCard key={key} emoji={emoji} title={title} content={sections[key]} delay={i * 120} highlight={highlight} />
        ) : null
      )}

      {paid ? (
        <>
          <div style={{
            margin: "16px 0 12px", padding: "14px 16px",
            background: "rgba(122,184,152,0.08)", border: "1px solid rgba(122,184,152,0.25)",
            borderRadius: "12px", textAlign: "center",
          }}>
            <p style={{ color: "#7ab898", fontFamily: "'DM Mono', monospace", fontSize: "11px", letterSpacing: "1px" }}>
              ✓ PRO PLAN ACTIVE — FULL PLAN UNLOCKED
            </p>
          </div>
          {lockedConfig.map(({ key, emoji, title }, i) =>
            sections[key] ? (
              <PlanCard key={key} emoji={emoji} title={title} content={sections[key]} delay={i * 120} />
            ) : null
          )}
        </>
      ) : (
        <>
          <div style={{
            margin: "16px 0 12px",
            padding: "20px",
            background: "linear-gradient(135deg, rgba(198,145,115,0.12), rgba(180,100,120,0.08))",
            border: `1px solid rgba(198,145,115,0.3)`,
            borderRadius: "12px",
            textAlign: "center",
          }}>
            <p style={{ color: T.textFaint, fontFamily: "'DM Mono', monospace", fontSize: "10px", letterSpacing: "2px", marginBottom: "10px" }}>
              {upsell.label}
            </p>
            <p style={{ color: T.cream, fontFamily: "'Cormorant Garamond', serif", fontSize: "22px", fontWeight: "700", marginBottom: "6px", lineHeight: 1.3 }}>
              {upsell.headline}
            </p>
            <p style={{ color: T.textFaint, fontFamily: "'DM Mono', monospace", fontSize: "11px", marginBottom: "16px", lineHeight: "1.7" }}>
              {upsell.sub}
            </p>
            <button onClick={() => setShowModal(true)} style={{
              padding: "14px 32px", borderRadius: "8px",
              background: `linear-gradient(135deg, ${T.rose}, ${T.roseLight})`,
              border: "none", color: "#0e0809",
              fontFamily: "'DM Mono', monospace", fontSize: "11px",
              fontWeight: "700", letterSpacing: "2px", cursor: "pointer",
              boxShadow: `0 4px 20px rgba(198,145,115,0.3)`,
              width: "100%", whiteSpace: "nowrap",
            }}>
              UNLOCK 4 STRATEGIES →
            </button>
            <p style={{ color: T.textFaint, fontFamily: "'DM Mono', monospace", fontSize: "10px", marginTop: "10px" }}>
              $0 for 7 days · then $19/mo · cancel anytime
            </p>
          </div>

          {lockedConfig.map(({ key, emoji, title }, i) => (
            <LockedCard
              key={key}
              emoji={emoji}
              title={title}
              teaser={TEASERS[key]}
              onUnlock={() => setShowModal(true)}
              delay={300 + i * 80}
            />
          ))}
        </>
      )}

      <button onClick={onReset} style={{
        marginTop: "16px", width: "100%", padding: "13px",
        background: "none", border: `1px solid ${T.border}`,
        borderRadius: "10px", color: T.textFaint,
        fontFamily: "'DM Mono', monospace", fontSize: "11px",
        letterSpacing: "1px", cursor: "pointer",
      }}>
        ↩ start over
      </button>
    </div>
  );
}

// ─── INTRO ────────────────────────────────────────────────────────────────────
// World-class fullscreen homepage. Renders outside the centered glass card.
// Replaces lines 1091-1346 of App.js.

// ─── WAITLIST FORM (product paused — replaces free-trial checkout CTAs) ──────
function WaitlistForm() {
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState("");
  const mono = "'DM Mono', monospace";

  const submit = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      setError("Please enter a valid email.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await fetch("/api/save-waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
    } catch (e) {}
    setSaving(false);
    setJoined(true);
  };

  if (joined) return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: "10px",
      background: "rgba(122,184,152,0.1)", border: "1px solid rgba(122,184,152,0.25)",
      borderRadius: "8px", padding: "16px 24px",
      color: T.green, fontFamily: mono, fontSize: "12px", letterSpacing: "1px",
    }}>
      ✓ You're on the list — we'll email you at launch.
    </div>
  );

  return (
    <div style={{ display: "inline-flex", flexDirection: "column", gap: "6px" }}>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "center" }}>
        <input
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={e => { setEmail(e.target.value); setError(""); }}
          onKeyDown={e => e.key === "Enter" && !saving && submit()}
          style={{
            padding: "15px 16px", minWidth: "220px",
            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(198,145,115,0.3)",
            borderRadius: "8px", color: T.cream, fontSize: "13px",
            fontFamily: mono, outline: "none",
          }}
        />
        <button onClick={submit} disabled={saving} style={{
          background: T.rose, color: "#1a0e10",
          fontFamily: mono, fontSize: "12px", fontWeight: 500,
          letterSpacing: "1.5px", textTransform: "uppercase",
          padding: "16px 28px", borderRadius: "8px", border: "none",
          cursor: saving ? "not-allowed" : "pointer",
          boxShadow: "0 8px 32px rgba(198,145,115,0.2), 0 0 0 1px rgba(255,255,255,0.05) inset",
        }}>
          {saving ? "Saving..." : "Join the waitlist, launching soon"}
        </button>
      </div>
      {error && (
        <span style={{ color: "#c4556a", fontFamily: mono, fontSize: "10px", letterSpacing: "0.5px" }}>{error}</span>
      )}
    </div>
  );
}

function Intro({ onStart }) {
  const [typingText, setTypingText] = useState("");
  const [showActions, setShowActions] = useState(false);
  const [statSubs, setStatSubs] = useState(0);
  const [statRev, setStatRev] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);
  const previewRef = useRef(null);
  const animatedRef = useRef(false);

  const messages = [
    "Filmed something after my workout last night that I almost didn't post. 4 minutes, one outfit, no edits — $15 to see why I'm a little embarrassed how good it came out.",
    "Couldn't sleep so I tried something I've never done on camera before. There's a moment in this I keep rewatching — $18 to see it.",
    "Spent the morning reading your messages. Made this one specifically thinking of you. $20 — don't open in public.",
    "Bath night turned into a 12-minute thing I really shouldn't have filmed. But I did. Yours for $22.",
  ];

  // Live-typing animation cycle
  useEffect(() => {
    let msgIdx = 0;
    let cancelled = false;
    let timeoutId;

    const typeMessage = (text) => {
      setShowActions(false);
      setTypingText("");
      let i = 0;
      const step = () => {
        if (cancelled) return;
        if (i < text.length) {
          setTypingText(text.substring(0, i + 1));
          i++;
          timeoutId = setTimeout(step, 18 + Math.random() * 24);
        } else {
          setShowActions(true);
          timeoutId = setTimeout(() => {
            msgIdx = (msgIdx + 1) % messages.length;
            typeMessage(messages[msgIdx]);
          }, 4500);
        }
      };
      step();
    };

    timeoutId = setTimeout(() => typeMessage(messages[0]), 800);
    return () => { cancelled = true; clearTimeout(timeoutId); };
  }, []);

  // Counting stat numbers when preview scrolls into view
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !animatedRef.current) {
          animatedRef.current = true;
          const duration = 1400;
          const start = Date.now();
          const tick = () => {
            const progress = Math.min((Date.now() - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setStatSubs(Math.floor(142 * eased));
            setStatRev(Math.floor(1847 * eased));
            if (progress < 1) requestAnimationFrame(tick);
          };
          tick();
        }
      });
    }, { threshold: 0.4 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const faqs = [
    {
      q: "Does this require my OnlyFans login?",
      a: "No, never. We don't store your OnlyFans credentials. The web app works entirely separately from your account — you copy what you need from MyOFCoach and paste it into OnlyFans yourself. You stay in control of your account at all times. When our optional Chrome extension launches, it will operate while you're already logged in to OnlyFans yourself — it won't ask for or store your password either."
    },
    {
      q: "Will my OnlyFans account get banned?",
      a: "No. Tools that automate posting or messaging on OnlyFans risk account suspension. MyOFCoach never automates anything on your account — we just help you write better messages and pick the right subreddits. You manually copy and send everything, which means zero ban risk on our end."
    },
    {
      q: "Why not just use a free AI chatbot?",
      a: "Most general AI chatbots refuse adult content or block OnlyFans-specific requests entirely. They also don't know OnlyFans-specific pricing, subreddit rules, or what actually converts on the platform. MyOFCoach is built specifically for OnlyFans creators — adult-content-friendly AI, pricing tables tailored to your niche and subscriber count, subreddit guidance, and message templates that match how creators actually write."
    },
    {
      q: "What if it doesn't work for me?",
      a: "You get 7 days free to try everything. If it's not for you, just cancel — one click, immediate. We don't make you talk to anyone or jump through hoops to leave. The trial gives you full access to every feature so you can decide based on real use."
    },
    {
      q: "How is this $29 when other tools cost $200+?",
      a: "Most OnlyFans tools are built for agencies managing dozens of creators, with infrastructure that justifies higher prices. MyOFCoach is built specifically for solo creators making under $3K/month — no agency features, no account-access infrastructure, no revenue-share tracking. Lower cost base means we can charge a flat $29 forever."
    },
    {
      q: "I'm just starting out. Will this help me?",
      a: "Yes — actually, the earlier you start using a real system, the faster you grow. The pricing calculator and subreddit hub matter most when you're 0-100 subs. The message templates compound as you add fans. Most creators benefit most when they're at 30-150 subs, exactly when good systems matter most."
    },
  ];

  // ─── STYLES ─────────────────────────────────────────────────────────────────
  const serif = "'Cormorant Garamond', serif";
  const mono = "'DM Mono', monospace";
  const sans = "'DM Sans', sans-serif";

  return (
    <div style={{ width: "100%", color: T.cream, fontFamily: sans }}>
      <style>{`
        .of-hero-h1 { font-size: clamp(44px, 8vw, 76px); }
        .of-section-h2 { font-size: clamp(32px, 5vw, 48px); }
        .of-hero-sub { font-size: clamp(16px, 2vw, 19px); }
        .of-app-grid { display: grid; grid-template-columns: 220px 1fr; min-height: 540px; }
        .of-stat-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
        .of-col-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .of-ba-grid { display: grid; grid-template-columns: 1fr 60px 1fr; gap: 24px; }
        .of-features { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
        .of-section-pad { padding: 120px 32px; }
        .of-hero-pad { padding: 80px 32px 80px; }
        .of-final-cta-pad { padding: 120px 32px; }
        .of-footer-pad { padding: 48px 32px; }
        .of-pricing-card { padding: 48px 40px; }
        .of-price-num { font-size: 80px; }
        .of-feature-card { padding: 32px; }
        .of-ba-card { padding: 36px 32px; }
        .of-mockup-pad { padding: 0 16px; }
        .of-mockup-url { max-width: 320px; }
        @media (max-width: 900px) {
          .of-app-grid { grid-template-columns: 1fr; }
          .of-sidebar { display: none !important; }
          .of-stat-row { grid-template-columns: 1fr; }
          .of-col-2 { grid-template-columns: 1fr; }
          .of-ba-grid { grid-template-columns: 1fr; }
          .of-ba-vs { padding: 8px !important; }
          .of-features { grid-template-columns: 1fr; }
        }
        @media (max-width: 768px) {
          .of-section-pad { padding: 72px 20px; }
          .of-hero-pad { padding: 64px 20px 56px; }
          .of-final-cta-pad { padding: 72px 20px; }
          .of-footer-pad { padding: 40px 20px; }
          .of-pricing-card { padding: 36px 24px; }
          .of-price-num { font-size: 64px; }
          .of-feature-card { padding: 24px; }
          .of-ba-card { padding: 28px 22px; }
          .of-mockup-pad { padding: 0 8px; }
          .of-mockup-url { max-width: 200px; font-size: 10px !important; }
        }
        @media (max-width: 480px) {
          .of-section-pad { padding: 56px 16px; }
          .of-hero-pad { padding: 56px 16px 48px; }
          .of-final-cta-pad { padding: 56px 16px; }
          .of-footer-pad { padding: 32px 16px; }
          .of-pricing-card { padding: 28px 18px; }
          .of-price-num { font-size: 56px; }
          .of-feature-card { padding: 20px; }
          .of-ba-card { padding: 22px 18px; }
          .of-mockup-pad { padding: 0 4px; }
          .of-mockup-url { max-width: 160px; font-size: 9px !important; letter-spacing: 0.2px !important; }
        }
        @keyframes ofcursor { 0%,50% { opacity:1; } 51%,100% { opacity:0; } }
        @keyframes oftypingdot { 0%,100% { opacity:0.4; } 50% { opacity:1; } }
      `}</style>

      {/* ============ HERO ============ */}
      <section className="of-hero-pad" style={{
        minHeight: "100vh",
        display: "flex", flexDirection: "column", alignItems: "center",
        position: "relative",
      }}>
        <div style={{
          maxWidth: "1100px", width: "100%", textAlign: "center",
          display: "flex", flexDirection: "column", alignItems: "center",
        }}>
          {/* Brand badge */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "8px",
            background: T.roseDim,
            border: `1px solid rgba(198,145,115,0.2)`,
            borderRadius: "100px", padding: "7px 16px",
            marginBottom: "28px",
            fontFamily: mono, fontSize: "11px", letterSpacing: "1px",
            color: T.rose, textTransform: "uppercase",
          }}>
            <span style={{ color: T.roseLight }}>✦</span>
            Built for solo creators · No account access
          </div>

          {/* H1 */}
          <h1 className="of-hero-h1" style={{
            fontFamily: serif, fontWeight: 600,
            lineHeight: 1.05, letterSpacing: "-1px",
            color: T.cream, maxWidth: "900px", marginBottom: "4px",
          }}>
            Most creators burn out<br/>
            <em style={{ fontStyle: "italic", fontWeight: 600, color: T.rose }}>
              before they ever grow.
            </em>
          </h1>

          {/* Sub */}
          <p className="of-hero-sub" style={{
            color: T.textMid, lineHeight: 1.6,
            maxWidth: "600px", margin: "24px 0 36px",
          }}>
            The flat-rate toolkit for solo creators making under $3K/month. Pricing, message templates, subreddit guidance, AI variations — without an agency, without account access.
          </p>

          {/* CTAs */}
          <div style={{
            display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap",
            justifyContent: "center", marginBottom: "24px",
          }}>
            <div id="waitlist">
              <WaitlistForm />
            </div>
            <a href="#preview" style={{
              color: T.textMid, textDecoration: "none",
              fontFamily: mono, fontSize: "12px",
              letterSpacing: "1.5px", textTransform: "uppercase",
              padding: "16px 24px", border: `1px solid ${T.border}`,
              borderRadius: "8px",
            }}>
              See It Inside
            </a>
          </div>

          {/* Trust line */}
          <div style={{
            display: "flex", gap: "28px", flexWrap: "wrap", justifyContent: "center",
            fontFamily: mono, fontSize: "10px", letterSpacing: "1.5px",
            color: T.textFaint, textTransform: "uppercase",
          }}>
            {["$29/mo flat", "No account access", "7-day free trial", "Cancel anytime"].map((t, i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <span style={{ color: T.green, fontSize: "12px" }}>✓</span>{t}
              </span>
            ))}
          </div>
        </div>

        {/* ============ PRODUCT PREVIEW ============ */}
        <div ref={previewRef} id="preview" className="of-mockup-pad" style={{
          width: "100%", maxWidth: "1100px", margin: "80px auto 0",
          position: "relative",
        }}>
          <div style={{
            background: "rgba(20,12,14,0.85)",
            border: `1px solid ${T.border}`, borderRadius: "14px",
            overflow: "hidden", backdropFilter: "blur(20px)",
            boxShadow: "0 0 0 1px rgba(255,255,255,0.03) inset, 0 32px 80px rgba(0,0,0,0.6), 0 0 60px rgba(198,145,115,0.06)",
            position: "relative",
          }}>
            {/* Browser chrome */}
            <div style={{
              display: "flex", alignItems: "center", gap: "12px",
              padding: "14px 18px",
              borderBottom: `1px solid ${T.border}`,
              background: "rgba(0,0,0,0.2)",
            }}>
              <div style={{ display: "flex", gap: "6px" }}>
                {[1,2,3].map(i => (
                  <span key={i} style={{
                    width: "11px", height: "11px", borderRadius: "50%",
                    background: "rgba(255,255,255,0.1)",
                  }}/>
                ))}
              </div>
              <div className="of-mockup-url" style={{
                flex: 1, fontFamily: mono, fontSize: "11px",
                color: T.textFaint, letterSpacing: "0.5px",
                textAlign: "center", padding: "4px 12px",
                background: "rgba(0,0,0,0.3)", borderRadius: "6px",
                margin: "0 auto",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                myofcoach.com<span style={{ color: T.textMid }}>/dashboard</span>
              </div>
              <div style={{ width: "50px" }}/>
            </div>

            {/* App grid */}
            <div className="of-app-grid">
              {/* Sidebar */}
              <aside className="of-sidebar" style={{
                background: "rgba(0,0,0,0.25)",
                borderRight: `1px solid ${T.border}`,
                padding: "24px 16px",
              }}>
                <div style={{
                  fontFamily: mono, fontSize: "9px", letterSpacing: "2px",
                  color: T.textFaint, textTransform: "uppercase",
                  padding: "8px 12px", marginBottom: "4px",
                }}>Main</div>
                {[
                  { label: "Today", active: true },
                  { label: "Pricing" },
                  { label: "Messages" },
                  { label: "Subreddits" },
                ].map((item, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: "10px",
                    padding: "10px 12px", marginBottom: "2px",
                    fontFamily: mono, fontSize: "11px", letterSpacing: "0.5px",
                    color: item.active ? T.rose : T.textMid,
                    textTransform: "uppercase", borderRadius: "6px",
                    borderLeft: `2px solid ${item.active ? T.rose : "transparent"}`,
                    background: item.active ? T.roseDim : "transparent",
                  }}>
                    <span style={{
                      width: "5px", height: "5px", borderRadius: "50%",
                      background: "currentColor", opacity: 0.6,
                    }}/>
                    {item.label}
                  </div>
                ))}

                <div style={{
                  fontFamily: mono, fontSize: "9px", letterSpacing: "2px",
                  color: T.textFaint, textTransform: "uppercase",
                  padding: "8px 12px", marginTop: "20px", marginBottom: "4px",
                }}>Library</div>
                {["Templates", "Saved"].map((label, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: "10px",
                    padding: "10px 12px", marginBottom: "2px",
                    fontFamily: mono, fontSize: "11px", letterSpacing: "0.5px",
                    color: T.textMid, textTransform: "uppercase",
                  }}>
                    <span style={{
                      width: "5px", height: "5px", borderRadius: "50%",
                      background: "currentColor", opacity: 0.6,
                    }}/>
                    {label}
                  </div>
                ))}
              </aside>

              {/* Main panel */}
              <main style={{ padding: "28px 32px", overflow: "hidden" }}>
                {/* Head */}
                <div style={{
                  display: "flex", justifyContent: "space-between",
                  alignItems: "flex-start", marginBottom: "24px",
                }}>
                  <div>
                    <div style={{
                      fontFamily: mono, fontSize: "10px", letterSpacing: "2px",
                      color: T.rose, textTransform: "uppercase", marginBottom: "6px",
                    }}>✦ Tuesday · May 1</div>
                    <h2 style={{
                      fontFamily: serif, fontSize: "28px", fontWeight: 600,
                      color: T.cream, letterSpacing: "-0.5px", lineHeight: 1.1,
                    }}>
                      Today's <em style={{ fontStyle: "italic", color: T.rose }}>playbook</em>
                    </h2>
                  </div>
                  <span style={{
                    fontFamily: mono, fontSize: "10px", letterSpacing: "1px",
                    color: T.green, textTransform: "uppercase",
                    padding: "5px 10px", borderRadius: "100px",
                    background: "rgba(122,184,152,0.1)",
                    border: "1px solid rgba(122,184,152,0.2)",
                  }}>● Live</span>
                </div>

                {/* Stats */}
                <div className="of-stat-row" style={{ marginBottom: "24px" }}>
                  {[
                    { label: "Subscribers", num: statSubs, sub: "+8 this week", up: true },
                    { label: "Revenue MTD", num: `$${statRev.toLocaleString()}`, sub: "+24% vs last mo", up: true, highlight: true },
                    { label: "Today's Tasks", num: "3 / 5", sub: "2 remaining" },
                  ].map((stat, i) => (
                    <div key={i} style={{
                      background: T.surface, border: `1px solid ${T.border}`,
                      borderRadius: "10px", padding: "16px",
                    }}>
                      <div style={{
                        fontFamily: mono, fontSize: "9px", letterSpacing: "1.5px",
                        color: T.textFaint, textTransform: "uppercase", marginBottom: "8px",
                      }}>{stat.label}</div>
                      <div style={{
                        fontFamily: serif, fontSize: "26px", fontWeight: 600,
                        color: stat.highlight ? T.rose : T.cream, lineHeight: 1,
                        fontStyle: stat.highlight ? "italic" : "normal",
                      }}>{stat.num}</div>
                      <div style={{
                        fontFamily: mono, fontSize: "10px",
                        color: stat.up ? T.green : T.textMid, marginTop: "6px",
                      }}>{stat.up && "↗ "}{stat.sub}</div>
                    </div>
                  ))}
                </div>

                {/* Two-column panels */}
                <div className="of-col-2">
                  {/* Pricing panel */}
                  <div style={{
                    background: T.surface, border: `1px solid ${T.border}`,
                    borderRadius: "10px", padding: "16px",
                  }}>
                    <div style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      marginBottom: "12px", paddingBottom: "10px",
                      borderBottom: `1px solid ${T.border}`,
                    }}>
                      <span style={{
                        fontFamily: mono, fontSize: "10px", letterSpacing: "1.5px",
                        color: T.cream, textTransform: "uppercase",
                      }}>Your Pricing — Fitness · 142 subs</span>
                    </div>
                    {[
                      { name: "Custom photo", val: "$15-22" },
                      { name: "Photo set (5)", val: "$30-45" },
                      { name: "Custom video — 3min", val: "$60-95" },
                      { name: "Sexting · 30 min", val: "$30-50" },
                      { name: "GFE · 1 day", val: "$75-125" },
                    ].map((row, i) => (
                      <div key={i} style={{
                        display: "flex", justifyContent: "space-between",
                        padding: "8px 0", fontFamily: mono, fontSize: "11px",
                        borderBottom: i < 4 ? "1px solid rgba(255,255,255,0.04)" : "none",
                      }}>
                        <span style={{ color: T.textMid }}>{row.name}</span>
                        <span style={{ color: T.rose, fontWeight: 500 }}>{row.val}</span>
                      </div>
                    ))}
                  </div>

                  {/* Subreddit panel */}
                  <div style={{
                    background: T.surface, border: `1px solid ${T.border}`,
                    borderRadius: "10px", padding: "16px",
                  }}>
                    <div style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      marginBottom: "12px", paddingBottom: "10px",
                      borderBottom: `1px solid ${T.border}`,
                    }}>
                      <span style={{
                        fontFamily: mono, fontSize: "10px", letterSpacing: "1.5px",
                        color: T.cream, textTransform: "uppercase",
                      }}>Subreddits Today</span>
                      <span style={{
                        fontFamily: mono, fontSize: "9px", letterSpacing: "1px",
                        color: T.rose, textTransform: "uppercase",
                      }}>View all →</span>
                    </div>
                    {[
                      { name: "r/FitGirls", meta: "Active · 24h limit", status: "Ready", ready: true },
                      { name: "r/OnlyFansGirls101", meta: "Active · 12h limit", status: "Ready", ready: true },
                      { name: "r/WorkoutGoneWild", meta: "Active · 24h limit", status: "8h cooldown", ready: false },
                      { name: "r/OnlyFansPromotions", meta: "Active · 24h limit", status: "22h", ready: false },
                    ].map((sub, i) => (
                      <div key={i} style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "10px 0",
                        borderBottom: i < 3 ? "1px solid rgba(255,255,255,0.04)" : "none",
                      }}>
                        <div>
                          <div style={{ fontFamily: mono, fontSize: "11px", color: T.cream }}>{sub.name}</div>
                          <div style={{ fontFamily: mono, fontSize: "9px", color: T.textFaint, letterSpacing: "0.5px" }}>{sub.meta}</div>
                        </div>
                        <span style={{
                          fontFamily: mono, fontSize: "9px", padding: "2px 8px",
                          borderRadius: "100px", letterSpacing: "0.5px",
                          color: sub.ready ? T.green : T.textFaint,
                          background: sub.ready ? "rgba(122,184,152,0.1)" : "rgba(255,255,255,0.04)",
                          border: sub.ready ? "1px solid rgba(122,184,152,0.2)" : `1px solid ${T.border}`,
                        }}>{sub.status}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Live-typing AI message */}
                <div style={{
                  background: "#14080c",
                  border: `1px solid ${T.borderHover}`,
                  borderRadius: "10px", padding: "14px 16px", marginTop: "12px",
                  boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
                }}>
                  <div style={{
                    fontFamily: mono, fontSize: "9px", letterSpacing: "1px",
                    color: T.rose, textTransform: "uppercase", marginBottom: "8px",
                    display: "flex", alignItems: "center", gap: "8px",
                  }}>
                    <span style={{
                      width: "5px", height: "5px", borderRadius: "50%",
                      background: T.rose, animation: "oftypingdot 1s infinite",
                    }}/>
                    ✦ AI is generating PPV caption...
                  </div>
                  <div style={{
                    fontFamily: serif, fontSize: "14px",
                    color: T.cream, lineHeight: 1.5, fontStyle: "italic",
                    minHeight: "42px",
                  }}>
                    {typingText}
                    <span style={{
                      display: "inline-block", width: "1px", height: "14px",
                      background: T.rose, marginLeft: "1px", verticalAlign: "middle",
                      animation: "ofcursor 1s infinite",
                    }}/>
                  </div>
                  <div style={{
                    display: "flex", gap: "8px", marginTop: "10px",
                    opacity: showActions ? 1 : 0, transition: "opacity 0.4s",
                  }}>
                    {[
                      { label: "Copy ↗", primary: true },
                      { label: "Regenerate" },
                      { label: "Save" },
                    ].map((btn, i) => (
                      <span key={i} style={{
                        fontFamily: mono, fontSize: "9px", letterSpacing: "1px",
                        color: btn.primary ? T.rose : T.textFaint,
                        textTransform: "uppercase",
                        padding: "5px 10px", borderRadius: "4px",
                        background: btn.primary ? T.roseDim : "rgba(255,255,255,0.04)",
                      }}>{btn.label}</span>
                    ))}
                  </div>
                </div>
              </main>
            </div>
          </div>
        </div>
      </section>

      {/* ============ BEFORE/AFTER ============ */}
      <section className="of-section-pad" style={{
        background: "linear-gradient(180deg, transparent, rgba(196,85,106,0.025), transparent)",
      }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{
            fontFamily: mono, fontSize: "11px", letterSpacing: "3px",
            color: T.rose, textTransform: "uppercase", marginBottom: "20px",
          }}>✦ Your Morning Routine</div>
          <h2 className="of-section-h2" style={{
            fontFamily: serif, fontWeight: 600,
            lineHeight: 1.15, letterSpacing: "-0.5px", color: T.cream,
            maxWidth: "720px", marginBottom: "20px",
          }}>
            3 hours of guessing,<br/>
            <em style={{ fontStyle: "italic", color: T.rose }}>or 5 minutes of executing.</em>
          </h2>

          <div className="of-ba-grid" style={{ marginTop: "48px" }}>
            {/* Before */}
            <div className="of-ba-card" style={{
              borderRadius: "14px",
              background: "rgba(196,85,106,0.04)",
              border: "1px solid rgba(196,85,106,0.15)",
            }}>
              <div style={{
                fontFamily: serif, fontWeight: 600, fontSize: "22px",
                marginBottom: "6px", lineHeight: 1.2, color: T.red,
              }}>Without MyOFCoach</div>
              <div style={{
                fontFamily: mono, fontSize: "11px", letterSpacing: "2px",
                textTransform: "uppercase", marginBottom: "24px", color: T.textFaint,
              }}>3+ hours every morning</div>
              <ul style={{ listStyle: "none", padding: 0 }}>
                {[
                  "Scroll Reddit guessing what to post",
                  "Open 12 tabs comparing pricing advice",
                  "Rewrite the same DMs from scratch",
                  "Forget which subs you've already posted to",
                  "Wonder if you're charging too little (you are)",
                  "Burn out by Wednesday, again",
                ].map((item, i, arr) => (
                  <li key={i} style={{
                    padding: "10px 0", display: "flex", gap: "12px", alignItems: "flex-start",
                    fontSize: "14px", color: T.textMid,
                    borderBottom: i < arr.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
                  }}>
                    <span style={{ color: T.red, fontWeight: 600, flexShrink: 0, width: "16px" }}>✕</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* VS */}
            <div className="of-ba-vs" style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: mono, fontSize: "12px", letterSpacing: "3px",
              color: T.textFaint,
            }}>VS</div>

            {/* After */}
            <div className="of-ba-card" style={{
              borderRadius: "14px",
              background: "rgba(122,184,152,0.04)",
              border: "1px solid rgba(122,184,152,0.2)",
            }}>
              <div style={{
                fontFamily: serif, fontWeight: 600, fontSize: "22px",
                marginBottom: "6px", lineHeight: 1.2, color: T.green,
              }}>With MyOFCoach</div>
              <div style={{
                fontFamily: mono, fontSize: "11px", letterSpacing: "2px",
                textTransform: "uppercase", marginBottom: "24px", color: T.textFaint,
              }}>5 minutes, done</div>
              <ul style={{ listStyle: "none", padding: 0 }}>
                {[
                  "Open app · see today's plan instantly",
                  "Copy 3 ready-to-send messages",
                  "Get pricing tailored to your niche",
                  "See which subreddits are ready (cooldown auto-tracked)",
                  "Generate fresh AI variations on demand",
                  "Close app · go live your life",
                ].map((item, i, arr) => (
                  <li key={i} style={{
                    padding: "10px 0", display: "flex", gap: "12px", alignItems: "flex-start",
                    fontSize: "14px", color: T.textMid,
                    borderBottom: i < arr.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
                  }}>
                    <span style={{ color: T.green, fontWeight: 600, flexShrink: 0, width: "16px" }}>✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section id="features" className="of-section-pad">
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{
            fontFamily: mono, fontSize: "11px", letterSpacing: "3px",
            color: T.rose, textTransform: "uppercase", marginBottom: "20px",
          }}>✦ Inside the Toolkit</div>
          <h2 className="of-section-h2" style={{
            fontFamily: serif, fontWeight: 600,
            lineHeight: 1.15, letterSpacing: "-0.5px", color: T.cream,
            maxWidth: "720px", marginBottom: "20px",
          }}>
            Four tools.<br/>
            Built to save you <em style={{ fontStyle: "italic", color: T.rose }}>2-3 hours every day.</em>
          </h2>
          <p style={{
            fontSize: "18px", color: T.textMid, lineHeight: 1.6,
            maxWidth: "600px", marginBottom: "56px",
          }}>
            Every feature works without touching your OnlyFans account. Copy what you need, paste into OnlyFans, send it yourself. You stay in control.
          </p>

          <div className="of-features">
            {[
              { icon: "$", title: "Pricing Calculator", desc: "Stop guessing what to charge. Input your niche, subscriber count, and content type. Get pricing ranges for PPV, customs, and tip menus tailored to your situation." },
              { icon: "✍", title: "Message Templates + AI", desc: "Hundreds of templates organized by situation, niche, and tone. AI generates fresh variations on demand. Save the ones that work — your personal library grows over time." },
              { icon: "r/", title: "Subreddit Hub", desc: "Top subreddits for your niche with rules built in. Member counts, posting time guidance, caption templates per sub. Cooldown tracker built in." },
              { icon: "◐", title: "Fan Tracking & CRM", desc: "Track your top spenders, set re-engagement reminders, and keep notes on each fan. Manual today, auto-populated when our Chrome extension launches." },
            ].map((feat, i) => (
              <div key={i} className="of-feature-card" style={{
                background: T.surface, border: `1px solid ${T.border}`,
                borderRadius: "14px",
                position: "relative", overflow: "hidden",
              }}>
                <div style={{
                  width: "44px", height: "44px",
                  background: T.roseDim,
                  border: "1px solid rgba(198,145,115,0.25)",
                  borderRadius: "10px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginBottom: "20px",
                  fontFamily: mono, fontSize: "16px", color: T.rose,
                }}>{feat.icon}</div>
                <h3 style={{
                  fontFamily: serif, fontSize: "24px", fontWeight: 600,
                  color: T.cream, marginBottom: "10px", letterSpacing: "-0.3px",
                }}>{feat.title}</h3>
                <p style={{
                  fontSize: "14px", color: T.textMid, lineHeight: 1.65,
                }}>{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRICING ============ */}
      <section id="pricing" className="of-section-pad">
        <div style={{ maxWidth: "1100px", margin: "0 auto", textAlign: "center" }}>
          <div style={{
            fontFamily: mono, fontSize: "11px", letterSpacing: "3px",
            color: T.rose, textTransform: "uppercase", marginBottom: "20px",
          }}>✦ One Price · Everything</div>
          <h2 className="of-section-h2" style={{
            fontFamily: serif, fontWeight: 600,
            lineHeight: 1.15, letterSpacing: "-0.5px", color: T.cream,
            margin: "0 auto 20px",
          }}>
            No tiers.<br/>
            <em style={{ fontStyle: "italic", color: T.rose }}>No revenue share.</em>
          </h2>

          <div className="of-pricing-card" style={{
            maxWidth: "520px", margin: "56px auto 0",
            background: T.surface, border: `1px solid ${T.borderHover}`,
            borderRadius: "16px",
            position: "relative",
            boxShadow: "0 32px 64px rgba(0,0,0,0.4), 0 0 60px rgba(198,145,115,0.08)",
          }}>
            <div style={{
              fontFamily: mono, fontSize: "10px", letterSpacing: "2px",
              color: T.rose, textTransform: "uppercase", marginBottom: "16px",
            }}>✦ Solo Creator Plan</div>
            <div style={{
              fontFamily: serif, fontWeight: 600,
              display: "flex", alignItems: "baseline", justifyContent: "center",
              gap: "6px", marginBottom: "8px",
            }}>
              <span className="of-price-num" style={{
                lineHeight: 1, color: T.cream, letterSpacing: "-2px",
              }}>$29</span>
              <span style={{
                fontFamily: mono, fontSize: "13px", letterSpacing: "1px",
                color: T.textFaint, textTransform: "uppercase",
              }}>/ month</span>
            </div>
            <div style={{
              fontFamily: serif, fontStyle: "italic",
              color: T.rose, fontSize: "16px", marginBottom: "24px",
            }}>flat — forever</div>

            {/* Math */}
            <div style={{
              background: "rgba(122,184,152,0.06)",
              border: "1px solid rgba(122,184,152,0.15)",
              borderRadius: "10px", padding: "18px 20px",
              marginBottom: "28px", textAlign: "left",
            }}>
              {[
                { label: "Time saved", val: "~14 hrs/week" },
                { label: "At $30/hour value", val: "$1,680/month" },
              ].map((row, i) => (
                <div key={i} style={{
                  display: "flex", justifyContent: "space-between",
                  padding: "6px 0", fontFamily: mono, fontSize: "12px",
                  color: T.textMid, letterSpacing: "0.3px",
                }}>
                  <span>{row.label}</span>
                  <span style={{ color: T.cream, fontWeight: 500 }}>{row.val}</span>
                </div>
              ))}
              <div style={{
                display: "flex", justifyContent: "space-between",
                borderTop: "1px solid rgba(122,184,152,0.2)",
                marginTop: "6px", padding: "12px 0 6px",
                fontFamily: mono, fontSize: "12px",
                color: T.green, fontWeight: 500,
              }}>
                <span>You pay</span>
                <span style={{ color: T.cream, fontWeight: 500 }}>$29/month</span>
              </div>
            </div>

            <ul style={{ listStyle: "none", textAlign: "left", marginBottom: "32px", padding: 0 }}>
              {[
                "Pricing calculator for your niche",
                "Message templates + AI variations",
                "Subreddit hub with conversion data",
                "Fan tracking & CRM (manual today, auto soon)",
                "Personal saved library that grows with you",
                "No account access required",
                "Cancel anytime",
              ].map((item, i) => (
                <li key={i} style={{
                  padding: "10px 0", fontSize: "14px", color: T.textMid,
                  display: "flex", gap: "10px", alignItems: "center",
                }}>
                  <span style={{ color: T.green, fontWeight: 600 }}>✓</span>
                  {item}
                </li>
              ))}
            </ul>

            <a href="#waitlist" style={{
              background: T.rose, color: "#1a0e10", textDecoration: "none",
              fontFamily: mono, fontSize: "12px", fontWeight: 500,
              letterSpacing: "1.5px", textTransform: "uppercase",
              padding: "16px 28px", borderRadius: "8px", border: "none",
              cursor: "pointer", display: "inline-flex",
              boxShadow: "0 8px 32px rgba(198,145,115,0.2), 0 0 0 1px rgba(255,255,255,0.05) inset",
            }}>
              Join the waitlist →
            </a>
            <div style={{
              fontFamily: mono, fontSize: "10px", letterSpacing: "1.5px",
              color: T.textFaint, textTransform: "uppercase", marginTop: "16px",
            }}>
              7-day free trial · Cancel anytime
            </div>
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="of-section-pad">
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{
            fontFamily: mono, fontSize: "11px", letterSpacing: "3px",
            color: T.rose, textTransform: "uppercase", marginBottom: "20px",
          }}>✦ Common Questions</div>
          <h2 className="of-section-h2" style={{
            fontFamily: serif, fontWeight: 600,
            lineHeight: 1.15, letterSpacing: "-0.5px", color: T.cream,
            maxWidth: "720px", marginBottom: "20px",
          }}>
            The things creators ask <em style={{ fontStyle: "italic", color: T.rose }}>before signing up.</em>
          </h2>

          <div style={{ maxWidth: "800px", margin: "56px auto 0" }}>
            {faqs.map((faq, i) => (
              <div key={i} style={{
                background: T.surface, border: `1px solid ${T.border}`,
                borderRadius: "12px", marginBottom: "12px", overflow: "hidden",
              }}>
                <div onClick={() => setOpenFaq(openFaq === i ? -1 : i)} style={{
                  padding: "20px 24px",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  cursor: "pointer",
                  fontFamily: serif, fontSize: "18px", fontWeight: 600,
                  color: T.cream,
                }}>
                  <span>{faq.q}</span>
                  <span style={{
                    width: "24px", height: "24px", borderRadius: "50%",
                    background: T.roseDim,
                    border: "1px solid rgba(198,145,115,0.3)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: T.rose, fontFamily: mono, fontSize: "14px",
                    transition: "transform 0.3s",
                    transform: openFaq === i ? "rotate(45deg)" : "rotate(0)",
                    flexShrink: 0, marginLeft: "16px",
                  }}>+</span>
                </div>
                <div style={{
                  maxHeight: openFaq === i ? "400px" : "0",
                  overflow: "hidden",
                  transition: "max-height 0.3s ease, padding 0.3s ease",
                  fontSize: "15px", color: T.textMid, lineHeight: 1.7,
                  padding: openFaq === i ? "0 24px 24px 24px" : "0 24px",
                }}>
                  {faq.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="of-final-cta-pad" style={{
        textAlign: "center",
        background: "radial-gradient(ellipse at center, rgba(198,145,115,0.08), transparent 70%)",
      }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <h2 className="of-section-h2" style={{
            fontFamily: serif, fontWeight: 600,
            lineHeight: 1.15, letterSpacing: "-0.5px", color: T.cream,
            margin: "0 auto 20px",
          }}>
            Stop guessing.<br/>
            <em style={{ fontStyle: "italic", color: T.rose }}>Start growing.</em>
          </h2>
          <p style={{
            fontSize: "18px", color: T.textMid, lineHeight: 1.6,
            margin: "0 auto 32px",
          }}>$29/month flat. No account access. Cancel anytime.</p>
          <a href="#waitlist" style={{
            background: T.rose, color: "#1a0e10", textDecoration: "none",
            fontFamily: mono, fontSize: "12px", fontWeight: 500,
            letterSpacing: "1.5px", textTransform: "uppercase",
            padding: "16px 28px", borderRadius: "8px", border: "none",
            cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "10px",
            boxShadow: "0 8px 32px rgba(198,145,115,0.2), 0 0 0 1px rgba(255,255,255,0.05) inset",
          }}>
            Join the waitlist →
          </a>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="of-footer-pad" style={{
        borderTop: `1px solid ${T.border}`,
        marginTop: "80px",
      }}>
        <div style={{
          maxWidth: "1280px", margin: "0 auto",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: "20px",
        }}>
          <div style={{
            fontFamily: serif, fontSize: "13px", letterSpacing: "4px",
            color: T.textFaint, textTransform: "uppercase",
          }}>✦ of coach ✦</div>
          <div style={{ display: "flex", gap: "28px", flexWrap: "wrap" }}>
            {[
              { label: "Features", href: "#features" },
              { label: "Pricing", href: "#pricing" },
              { label: "Blog", href: "/blog/" },
              { label: "Privacy", href: "/privacy/" },
              { label: "Terms", href: "/terms/" },
            ].map((link, i) => (
              <a key={i} href={link.href} style={{
                fontFamily: mono, fontSize: "11px", letterSpacing: "1.5px",
                color: T.textFaint, textTransform: "uppercase",
                textDecoration: "none",
              }}>{link.label}</a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}


// ─── APP ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [phase, setPhase] = useState("intro");
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const path = window.location.pathname;
    const hash = window.location.hash;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        window.history.replaceState({}, "", "/dashboard");
        setPhase("dashboard");
      }
    });

    if (params.get("paid") === "true" || (path === "/dashboard" && !hash.includes("access_token"))) {
      window.history.replaceState({}, "", "/dashboard");
      setPhase("dashboard");
      return () => subscription.unsubscribe();
    }

    if (hash.includes("access_token")) {
      const hashParams = new URLSearchParams(hash.replace("#", ""));
      const accessToken = hashParams.get("access_token");
      const refreshToken = hashParams.get("refresh_token");
      if (accessToken && refreshToken) {
        supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
          .then(({ data: { session } }) => {
            if (session) {
              window.history.replaceState({}, "", "/dashboard");
              setPhase("dashboard");
            }
          });
      }
      return () => subscription.unsubscribe();
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        window.history.replaceState({}, "", "/dashboard");
        setPhase("dashboard");
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const scrollTop = () => {
    if (cardRef.current) cardRef.current.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAnswer = async (id, value) => {
    const next = { ...answers, [id]: value };
    setAnswers(next);
    scrollTop();

    if (stepIdx < STEPS.length - 1) {
      setStepIdx(i => i + 1);
    } else {
      setPhase("loading");
      try {
        const raw = await fetchPlan(next);
        const parsed = parsePlan(raw);
        window.__ofcoach_plan = parsed;
        window.__ofcoach_answers = next;
        setPlan(parsed);
        setPhase("plan");
        // fire-and-forget — tracks quiz completions without blocking UI
        try {
          supabase.from("quiz_completions").insert({
            session_id: crypto.randomUUID(),
            answers: next,
          }).then(() => {});
        } catch (e) {}
      } catch (e) {
        setError("Something went wrong. Please try again.");
        setPhase("error");
      }
    }
  };

  const reset = () => {
    setPhase("intro");
    setStepIdx(0);
    setAnswers({});
    setPlan(null);
    setError(null);
    scrollTop();
  };

  const previewFree = new URLSearchParams(window.location.search).get("preview") === "free";

  if (phase === "dashboard" && !previewFree) return <Dashboard />;

  if (phase === "intro") {
    return (
      <div style={{
        minHeight: "100vh",
        background: T.bg,
        backgroundImage: `
          radial-gradient(ellipse 1200px 800px at 50% -10%, rgba(198,145,115,0.12), transparent 60%),
          radial-gradient(ellipse 800px 600px at 90% 40%, rgba(122,184,152,0.05), transparent 60%),
          radial-gradient(ellipse 600px 500px at 10% 80%, rgba(198,145,115,0.06), transparent 60%)
        `,
        color: T.cream,
        overflowX: "hidden",
      }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600;1,700&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500;600&display=swap');
          *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
          html { scroll-behavior: smooth; }
          body { -webkit-font-smoothing: antialiased; }
          a { color: inherit; text-decoration: none; }
          button { font-family: inherit; cursor: pointer; }
        `}</style>

        {/* Top nav */}
        <nav style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
          background: "rgba(14, 8, 9, 0.7)",
          backdropFilter: "blur(24px) saturate(180%)",
          WebkitBackdropFilter: "blur(24px) saturate(180%)",
          borderBottom: `1px solid ${T.border}`,
        }}>
          <div style={{
            maxWidth: "1280px", margin: "0 auto", padding: "16px 32px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <a href="/" style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "14px", letterSpacing: "4px",
              color: T.textFaint, textTransform: "uppercase",
              fontWeight: 500, textDecoration: "none",
            }}>
              <span style={{ color: T.rose, fontWeight: 600 }}>✦</span> of coach <span style={{ color: T.rose, fontWeight: 600 }}>✦</span>
            </a>
            <button onClick={() => { window.history.pushState({}, "", "/dashboard"); setPhase("dashboard"); }} style={{
              background: "none",
              fontFamily: "'DM Mono', monospace",
              fontSize: "10px", letterSpacing: "2px",
              color: T.rose, textTransform: "uppercase",
              padding: "8px 18px", border: "1px solid rgba(198,145,115,0.3)",
              borderRadius: "6px", cursor: "pointer",
            }}>
              Sign In
            </button>
          </div>
        </nav>

        <Intro />
        <Analytics />
      </div>
    );
  }

  // ─── OTHER PHASES RENDER INSIDE THE CENTERED GLASS CARD (UNCHANGED) ──────
  return (
    <div style={{
      minHeight: "100vh",
      background: T.bg,
      backgroundImage: `
        radial-gradient(ellipse at 15% 15%, rgba(198,145,115,0.06) 0%, transparent 55%),
        radial-gradient(ellipse at 85% 85%, rgba(180,100,120,0.05) 0%, transparent 55%),
        radial-gradient(ellipse at 50% 50%, rgba(198,145,115,0.02) 0%, transparent 80%)
      `,
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: "clamp(12px,3vw,40px) clamp(12px,4vw,16px) 40px",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes ofpulse { 0%,100% { box-shadow:0 0 0 3px rgba(122,184,152,0.25); } 50% { box-shadow:0 0 0 6px rgba(122,184,152,0.05); } }
        button { transition: opacity 0.15s, transform 0.15s; -webkit-tap-highlight-color: transparent; }
        button:hover:not(:disabled) { opacity: 0.85; }
        button:active:not(:disabled) { transform: scale(0.98) !important; }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-thumb { background: rgba(198,145,115,0.25); border-radius: 2px; }
        input, button { font-size: 16px !important; }
        @media (max-width: 480px) {
          .stats-grid { grid-template-columns: repeat(3,1fr) !important; }
          .option-grid { grid-template-columns: 1fr !important; }
          .card-pad { padding: 20px 16px !important; }
        }
      `}</style>

      <div style={{ width: "100%", maxWidth: "500px" }}>
        {/* Wordmark + Login */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
          <span style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "13px", letterSpacing: "4px",
            color: T.textFaint, textTransform: "uppercase",
          }}>
            ✦ of coach ✦
          </span>
          <button onClick={() => { window.history.pushState({}, "", "/dashboard"); setPhase("dashboard"); }} style={{
            background: "none", border: `1px solid rgba(198,145,115,0.25)`,
            borderRadius: "6px", color: T.rose,
            fontFamily: "'DM Mono', monospace", fontSize: "9px",
            padding: "6px 14px", cursor: "pointer", letterSpacing: "1.5px",
          }}>
            LOGIN
          </button>
        </div>

        {/* Card */}
        <div
          ref={cardRef}
          className="card-pad"
          style={{
            background: "rgba(20,12,14,0.85)",
            border: `1px solid ${T.border}`,
            borderRadius: "16px",
            padding: "clamp(20px,5vw,36px)",
            backdropFilter: "blur(20px)",
            boxShadow: `
              0 0 0 1px rgba(255,255,255,0.03) inset,
              0 32px 64px rgba(0,0,0,0.5),
              0 0 40px rgba(198,145,115,0.04)
            `,
          }}
        >
          {phase === "onboarding" && (
            <>
              <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                <button
                  onClick={() => {
                    if (stepIdx === 0) { setPhase("intro"); }
                    else { setStepIdx(i => i - 1); }
                  }}
                  style={{
                    background: "none", border: "none",
                    color: T.textFaint, cursor: "pointer",
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "11px", letterSpacing: "1px",
                    padding: "4px 0", display: "flex",
                    alignItems: "center", gap: "6px",
                  }}
                >
                  ← BACK
                </button>
              </div>
              <Progress step={stepIdx + 1} total={STEPS.length} />
              <Step
                step={STEPS[stepIdx]}
                onAnswer={handleAnswer}
                answers={answers}
              />
            </>
          )}

          {phase === "loading" && <Loader />}

          {phase === "plan" && plan && (
            <Plan sections={plan} answers={answers} onReset={reset} paid={false} />
          )}

          {phase === "error" && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <p style={{ color: T.red, fontFamily: "'DM Mono', monospace", fontSize: "13px", marginBottom: "20px" }}>
                {error}
              </p>
              <button onClick={reset} style={{
                padding: "12px 24px", borderRadius: "8px",
                background: T.roseDim, border: `1px solid ${T.rose}`,
                color: T.rose, fontFamily: "'DM Mono', monospace",
                fontSize: "11px", letterSpacing: "1px", cursor: "pointer",
              }}>
                TRY AGAIN
              </button>
            </div>
          )}
        </div>

        <p style={{
          textAlign: "center", color: T.textFaint,
          fontSize: "10px", fontFamily: "'DM Mono', monospace",
          marginTop: "16px", letterSpacing: "0.5px",
        }}>
          Strategy advice only · Not affiliated with OnlyFans
        </p>
      </div>

      <Analytics />
    </div>
  );
}
